import "./SideMenuComponent.scss";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar
} from "@/components/ui/sidebar.tsx";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Files, Inbox, Zap, X } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import { EXPLORE_APPS_ROUTE, PAYMENTS_ROUTE, PROJECTS_ROUTE } from "@/constants/RoutesConfig.tsx";
import { cn } from "@/lib/utils.ts";
import { useAppSelector } from "@/store/hooks.ts";
import {ReactNode} from "react";
import { CommonService } from "@/services";
import { useIsMobile } from "@/hooks/use-mobile";
import { ImageConfig } from "@/constants";

const items = [
    {
        title: "Projects",
        url: PROJECTS_ROUTE,
        icon: Files,
    },
    {
        title: "Explore Apps",
        url: EXPLORE_APPS_ROUTE,
        icon: Inbox,
    },
];

type SidebarNavItemProps = {
    item: {
        title: string;
        url: string;
        icon: React.ElementType;
    };
    isFirstItem: boolean;
    isRouteActive: boolean;
};

const SidebarNavItem = ({ item, isFirstItem, isRouteActive }: SidebarNavItemProps) => {
    const location = useLocation();
    // Simple exact path matching without using hooks that were causing issues
    const isActive = location.pathname === item.url;
    const itemShouldBeActive = isActive || (isFirstItem && !isRouteActive);

    return (
        <SidebarMenuItem className="group/sidebar-item mb-1.5">
            <SidebarMenuButton variant="default" asChild>
                <NavLink
                    to={item.url}
                    className={cn(
                        "flex items-center gap-2.5 w-full py-2 px-3 rounded-md",
                        itemShouldBeActive
                            ? "bg-sidebar-accent text-primary-foreground"
                            : "hover:bg-primary/5"
                    )}
                    onClick={() => {
                        CommonService.handleMixpanelTrack(`${item.title} clicked`, {
                            item: item.title,
                            timestamp: new Date().toISOString()
                        });
                    }}
                >
                    <item.icon
                        strokeWidth={2}
                        className={cn(
                            itemShouldBeActive
                                ? "text-primary"
                                : "text-secondary group-hover/sidebar-item:text-primary"
                        )}
                    />
                    <span
                        className={cn(
                            "text-secondary text-[16px]",
                            itemShouldBeActive ? "font-medium" : "font-normal"
                        )}
                    >
                        {item.title}
                    </span>
                </NavLink>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
};

// Mobile header component for the sidebar
const MobileSidebarHeader = () => {
    const { setOpenMobile } = useSidebar();
    const isMobile = useIsMobile();

    if (!isMobile) return null;

    return (
        <div className="mobile-sidebar-header flex items-center justify-between p-4 border-b border-border bg-background">
            <div className="flex items-center gap-2">
                <ImageConfig.LogoSmallIcon className="h-6 w-6" />
                <span className="text-sm font-medium text-foreground">Menu</span>
            </div>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpenMobile(false)}
                className="h-8 w-8 p-0 hover:bg-accent"
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
};

const SideMenuComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = useAppSelector((state) => state.auth?.user);

    // Check if any route is active by directly comparing with current location
    const isRouteActive = items.some(item => location.pathname === item.url);

    return (
        <>
            {/* Desktop Sidebar */}
            <div className="side-menu-component hidden md:block">
                <Sidebar className="h-[calc(100vh-60px)] fixed top-[60px]">
                    <SidebarContent>
                        <SidebarGroup>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    {items.map((item, index) => (
                                        <SidebarNavItem
                                            key={item.title}
                                            item={item}
                                            isFirstItem={index === 0}
                                            isRouteActive={isRouteActive}
                                        />
                                    )) as ReactNode}
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    </SidebarContent>
                    <SidebarFooter>
                        <div className="font-medium plan-title ml-3 mb-4">
                        {currentUser?.subscription?.plan_name ? "You're on  " +CommonService.capitalizeFirstLetter(currentUser?.subscription?.plan_name) + " Plan"  :  "You're on Free Plan"}
                        </div>
                      
                        <Button
                            fullWidth
                            color="primary"
                            className="gap-2"
                            mixpanelEvent={{
                                name: "upgrade_button_clicked"
                            }}
                            onClick={() => {
                                navigate(PAYMENTS_ROUTE);
                            }}
                        >
                            <Zap className="h-4 w-4" />
                            <span>Upgrade Plan</span>
                        </Button>
                    </SidebarFooter>
                </Sidebar>
            </div>

            {/* Mobile Sidebar */}
            <div className="side-menu-component md:hidden">
                <Sidebar>
                    <MobileSidebarHeader />
                    <SidebarContent>
                        <SidebarGroup>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    {items.map((item, index) => (
                                        <SidebarNavItem
                                            key={item.title}
                                            item={item}
                                            isFirstItem={index === 0}
                                            isRouteActive={isRouteActive}
                                        />
                                    )) as ReactNode}
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    </SidebarContent>
                    <SidebarFooter>
                        <div className="font-medium plan-title ml-3 mb-4">
                        {currentUser?.subscription?.plan_name ? "You're on  "+CommonService.capitalizeFirstLetter(currentUser?.subscription?.plan_name) + " Plan"  :  "You're on Free Plan"}
                        </div>
                        
                        <Button
                            fullWidth
                            color="primary"
                            className="gap-2"
                            mixpanelEvent={{
                                name: "upgrade_button_clicked"
                            }}
                            onClick={() => {
                                navigate(PAYMENTS_ROUTE);
                            }}
                        >
                            <Zap className="h-4 w-4" />
                            <span>Upgrade Plan</span>
                        </Button>
                    </SidebarFooter>
                </Sidebar>
            </div>
        </>
    );
};

export default SideMenuComponent;
